import React from "react"
import type {
  DepartmentDimension,
  LocationDimension,
} from "@digits-graphql/frontend/graphql-bearer"
import { hasFirstElement } from "@digits-shared/helpers/arrayHelper"
import { useComponentContext } from "src/frontend/components/OS/Applications/Reports/Report/Viewer/Layout/LayoutViewer/ComponentContext"
import { toTimeseries } from "src/frontend/components/Shared/Layout/Components/Charts/toTimeseries"
import type { DepartmentSummaries } from "src/frontend/components/Shared/Layout/Components/Departments/filterDepartmentAncestors"
import { useCombinedDepartmentsSeries } from "src/frontend/components/Shared/Layout/Components/Departments/useCombinedDepartmentsSeries"
import { useTopDepartmentsSeries } from "src/frontend/components/Shared/Layout/Components/Departments/useTopDepartmentsSeries"
import type { LocationSummaries } from "src/frontend/components/Shared/Layout/Components/Locations/filterLocationAncestors"
import { useTopLocationsSeries } from "src/frontend/components/Shared/Layout/Components/Locations/useTopLocationsSeries"
import {
  type StatementCategoryDetails,
  type SummaryLineRowDetails,
} from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { useComponentConfigTitle } from "src/frontend/components/Shared/Layout/Components/useComponentTitle"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

export function useClassBasedChartData(details: StatementCategoryDetails | SummaryLineRowDetails) {
  const intervalOrigin = useIntervalOrigin()
  const { component } = useComponentContext()
  const { periodName } = useComponentConfigTitle({ config: component.config })
  const { department, location } = getClassDimensionData(details)
  const chartName = getChartName(details)

  const departmentsSeries = useTopDepartmentsSeries(
    React.useMemo(() => getDepartmentSummaries(department), [department])
  )
  const locationsSeries = useTopLocationsSeries(
    React.useMemo(() => getLocationSummaries(location), [location])
  )
  const timeSeries = React.useMemo(() => {
    const createTimeSeries = (items: DepartmentDimension[] | LocationDimension[]) =>
      items.map(({ by }) => toTimeseries(chartName, by.time.map((t) => t.summary).reverse()))

    if (hasFirstElement(department)) return createTimeSeries(department)
    if (hasFirstElement(location)) return createTimeSeries(location)
    return []
  }, [department, location, chartName])
  const amountOverTime = useCombinedDepartmentsSeries(timeSeries) ?? []

  const data = React.useMemo(
    () => (hasFirstElement(departmentsSeries.summaries) ? departmentsSeries : locationsSeries),
    [departmentsSeries, locationsSeries]
  )

  return {
    periodName,
    amountOverTime,
    data,
    intervalOrigin: component.config.statement?.origin || intervalOrigin,
  }
}

function getDepartmentSummaries(department?: DepartmentDimension[]): DepartmentSummaries[] {
  if (!hasFirstElement(department)) return []

  return department.map(({ departmentObject, by }) => ({
    label: departmentObject.name,
    summaries: by.time.map((t) => t.summary).reverse(),
    department: departmentObject,
  }))
}

function getLocationSummaries(locations?: LocationDimension[]): LocationSummaries[] {
  if (!hasFirstElement(locations)) return []

  return locations.map(({ locationObject, by }) => ({
    label: locationObject.name,
    summaries: by.time.map((t) => t.summary).reverse(),
    location: locationObject,
  }))
}

function getChartName(details: StatementCategoryDetails | SummaryLineRowDetails): string {
  return isCategoryDetails(details)
    ? details.hover.entity.name
    : details.periodDetails.hover.lineType
}

function getClassDimensionData(details: StatementCategoryDetails | SummaryLineRowDetails) {
  if (isCategoryDetails(details)) {
    const { department, location } = details.hover.history
    return { department, location }
  }
  const { department, location } = details.periodDetails.hover.history
  return { department, location }
}

function isCategoryDetails(
  details: StatementCategoryDetails | SummaryLineRowDetails
): details is StatementCategoryDetails {
  return "hover" in details && "entity" in details.hover
}
